import { zustandImmerMiddleware } from '@sketchnote/shared-components';
import debounce from 'lodash/debounce';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import {
  addOptionsFormField,
  createFormField,
  createFormToTask,
  deleteFormField,
  deleteOptionsFormField,
  getForm,
  getLiveForm,
  getPublicForm,
  unlinkFormToTask,
  updateForm,
  updateFormField,
  updateFormProperties,
  updateFormToTask,
} from '../api/useFormServices';
import { Form, FormField, FormOption, FormToTaskMap } from '../types/form';

const initialState = {
  forms: {},
  currentFormId: undefined,
  isFullScreen: false,
  currentFormView: 'create',
} as const;

export const useFormsStore = create<
  FormsStore,
  [['zustand/devtools', FormsStore], ['zustand/immer', FormsStore]]
>(
  devtools(
    zustandImmerMiddleware((set, get) => ({
      ...initialState,

      setForm: (id) => {
        set((state) => {
          const { forms } = state;
          forms[id] = { id };
        });
      },

      resultView: 'inbox',
      setResultView: (view) =>
        set((s) => {
          s.resultView = view;
        }),

      totalResponseCount: 0,
      setTotalResponseCount: (value) =>
        set((s) => {
          s.totalResponseCount = value;
        }),

      fetchForm: async (formId) => {
        set((state) => ({ ...state, isFetching: true }));
        set((state) => {
          state.isFetching = true;
        });
        set((state) => {
          const { forms } = state;
          forms[formId] = { ...forms[formId], isFetching: true };
        });
        const doc = await getForm({ formId });
        if (doc) {
          set((state) => {
            state.forms[formId] = { ...state.forms[formId], ...doc, isFetching: false };
          });
          return doc;
        }
      },

      fetchLiveForm: async (formId) => {
        set((state) => ({ ...state, isFetching: true }));
        set((state) => {
          state.isFetching = true;
        });
        set((state) => {
          const { forms } = state;
          forms[formId] = { ...forms[formId], isFetching: true };
        });
        const doc = await getLiveForm({ formId });
        if (doc) {
          set((state) => {
            state.forms[formId] = { ...state.forms[formId], ...doc, isFetching: false };
          });
          return doc;
        }
      },

      fetchPublicForm: async (formId) => {
        set((state) => ({ ...state, isFetching: true }));
        set((state) => {
          state.isFetching = true;
        });
        set((state) => {
          const { forms } = state;
          forms[formId] = { ...forms[formId], isFetching: true };
        });
        const doc = await getPublicForm({ formId });
        if (doc) {
          set((state) => {
            state.forms[formId] = { ...state.forms[formId], ...doc, isFetching: false };
          });
        }
      },
      onChangeForm: (formKey, value, formId) => {
        set((state) => {
          const { forms } = state;
          const form = forms[formId];
          if (form) {
            saveFormFields(formKey, value, formId);
            form[formKey] = value;
          }
        });
      },
      onAddNewFormField: async (formId, index) => {
        set((state) => {
          const { forms } = state;
          const form = forms[formId];
          if (form) {
            if (!form.fields) {
              form.fields = [];
            }
            index = index || form?.fields?.length || 0;
            form.fields?.splice(index, 0, { _id: '', responseType: 'short-text' });
          }
        });

        const res = await createFormField({
          formId,
          field: { responseType: 'short-text' },
          index,
        });

        set((state) => {
          const { forms } = state;
          const form = forms[formId];
          index = index || 0;
          if (form) {
            const field = form?.fields?.[index];
            if (field) {
              field._id = res._id;
            }
          }
        });
        return res;
      },

      onChangeFormField: (key, value, formId, index, error) => {
        set((state) => {
          const { forms } = state;
          const form = forms[formId];
          const field = form?.fields?.[index];

          if (form && field?._id) {
            field[key] = value;

            if (!error) saveField(key, value, field._id, index, formId);
          }
        });
      },

      onDeleteFormFieldOptions: (value, formId, index) => {
        set((state) => {
          const { forms } = state;
          const form = forms[formId];
          const field = form?.fields?.[index];
          const currentOptions = field?.['options'] || [];

          if (form && field?._id) {
            const data = currentOptions.filter((item) => {
              return item.optionId !== value.optionId;
            });
            field['options'] = data;
            deleteOption('options', value, field._id, index, formId);
          }
        });
      },

      onChangeFormFieldOptions: (value, formId, index) => {
        set((state) => {
          const { forms } = state;
          const form = forms[formId];
          const field = form?.fields?.[index];
          if (field?._id) {
            const currentOptions = field?.['options'] || [];
            if (form && field?._id) {
              field['options'] = value;
            }
            saveOptions('options', value, field._id, index, formId);
          }
        });
      },

      onChangeMultipleFormField: (fieldProperties, formId, index) => {
        set((state) => {
          const { forms } = state;
          const form = forms[formId];
          const field = form?.fields?.[index];

          if (form && field?._id) {
            Object.keys(fieldProperties).map((item) => {
              return (field[item] = fieldProperties[item]);
            });
            saveMultipleFormField(fieldProperties, field._id, index, formId);
          }
        });
      },

      onRemoveFormField: async (formId, index) => {
        set((state) => {
          const { forms } = state;
          const form = forms[formId];
          if (form) {
            const field = form.fields?.[index];
            form.fields?.splice(index, 1);
            // TODO: handle the failure case
            field?._id && deleteFormField({ fieldId: field?._id });
          }
        });
      },
      onMoveFormField: (formId, oldIndex, newIndex) => {
        set((state) => {
          const { forms } = state;
          const form = forms[formId];
          if (form && form.fields?.[oldIndex]) {
            const movedField = form.fields[oldIndex];
            form.fields.splice(oldIndex, 1);
            form.fields.splice(newIndex, 0, movedField);
            movedField._id && saveField('index', newIndex, movedField._id, oldIndex, formId);
          }
        });
      },

      onUpdateFormProperties: async ({ id, title, icon, coverImage, description, showCover }) => {
        set((state) => {
          const { forms } = state;
          const form = forms[id];
          if (form) {
            if (title) form.title = title;
            if (icon) form.icon = icon;
            if (coverImage) form.cover = coverImage;
            if (description) form.description = description;
            if (showCover !== undefined) {
              form.showCover = showCover;
            }
          }
        });

        const fileResponse = await updateFormProperties({
          id,
          title,
          icon,
          coverImage,
          description,
          showCover,
        });

        return fileResponse;
      },
      createFormToTask,
      updateFormToTask,
      unlinkFormToTask,
      setFormToTaskData: (formId, data) => {
        set((state) => {
          const { forms } = state;
          const form = forms[formId];
          if (form) {
            form.formToTaskMap = data;
          }
        });
      },
    }))
  )
);

const saveFormFields = debounce(
  async <T extends keyof Form>(key: T, value: Form[T], formId: string) => {
    const form = await updateForm({ formData: { [key]: value }, formId });

    // useFormsStore.setState((state) => {
    //   state.forms[formId] = { ...state.forms[formId], ...form };
    // });
  },
  500
);

const deleteOption = debounce(
  async (key: any, value: any, fieldId: string, index: number, formId: string) => {
    const formResponse = await deleteOptionsFormField(value.optionId);
    // useFormsStore.setState((state) => {
    //   const { forms } = state;
    //   const form = forms[formId];
    //   const field = form?.fields?.[index];
    //   if (form && field?._id && formResponse?._id) {
    //     field['options'] = formResponse['options'];
    //   }
    // });

    const data = useFormsStore.getState().forms[formId];
  },
  1000
);
const saveOptions = debounce(
  async (key: any, value: any, fieldId: string, index: number, formId: string) => {
    const payload = value.map((item, index) => {
      return { ...item, fieldId, index };
    });

    const formResponse = await addOptionsFormField({ payload });

    // useFormsStore.setState((state) => {
    //   const { forms } = state;
    //   const form = forms[formId];
    //   const field = form?.fields?.[index];
    //   if (form && field?._id) {
    //     field['options'] = formResponse['options'];
    //   }
    // });

    const data = useFormsStore.getState().forms[formId];
  },
  1000
);

const saveField = debounce(
  //TODO update key type
  async (key: any, value: any, fieldId: string, index: number, formId: string) => {
    const formResponse = await updateFormField({ fieldId, formField: { [key]: value } });

    const data = useFormsStore.getState().forms[formId];

    // useFormsStore.setState((state) => {
    //   const { forms } = state;
    //   const form = forms[formId];
    //   const field = form?.fields?.[index];
    //   if (form && field?._id) {
    //     field[key] = formResponse[key];
    //   }
    // });
  },
  1000
);

const saveMultipleFormField = async <T extends keyof FormField>(
  fieldProperties: FormField,
  fieldId: string,
  index: number,
  formId: string
) => {
  const formResponse = await updateFormField({ fieldId, formField: fieldProperties });
};

export interface FormsStore {
  forms: {
    [key: string]: Form | undefined;
  };
  currentFormId?: string;
  currentFormView?: 'create' | 'preview' | 'results';
  isFetching?: boolean;
  setForm: (id: string) => void;
  fetchForm: (formId: string) => Promise<void>;
  fetchLiveForm: (formId: string) => Promise<void>;

  totalResponseCount: number;
  setTotalResponseCount: (value: number) => void;

  resultView: string;
  setResultView: (view: string) => void;

  fetchPublicForm: (formId: string) => Promise<void>;

  onChangeForm: <T extends keyof Form>(key: T, value: Form[T], formId: string) => void;
  onAddNewFormField: (formId: string, index?: number) => void;
  onRemoveFormField: (formId: string, index: number) => void;
  onChangeFormFieldOptions: <T extends keyof FormField>(
    value: FormOption[],
    formId: string,
    index: number
  ) => void;
  onUpdateFormProperties: ({
    id,
    title,
    icon,
    coverImage,
    description,
    showCover,
  }: {
    id: string;
    title?: string;
    icon?: any;
    coverImage?: string;
    description?: string;
    showCover?: boolean;
  }) => void;
  onDeleteFormFieldOptions: <T extends keyof FormField>(
    value: FormOption,
    formId: string,
    index: number
  ) => void;
  onChangeFormField: <T extends keyof FormField>(
    key: T,
    value: FormField[T],
    formId: string,
    index: number,
    error?: boolean
  ) => void;

  onChangeMultipleFormField: (fieldProperties: FormField, formId: string, index: number) => void;
  createFormToTask: ({
    fileId,
    taskFileTitle,
    workspaceId,
    fieldId,
  }: {
    fileId: string;
    taskFileTitle: string;
    workspaceId: string;
    fieldId: string;
  }) => any;
  updateFormToTask: ({ formId, fieldId }: { formId: string; fieldId: string }) => any;
  unlinkFormToTask: (formId: string) => any;
  setFormToTaskData: (formId: string, data?: FormToTaskMap) => void;
  onMoveFormField: (formId: string, oldIndex: number, newIndex: number) => void;
}
